// "Welcome Day" — a first-year orientation experience: a time-based itinerary of
// events (each a pin on the map) plus a "backpack" of common student resources.
//
// Data lives in Vercel KV in production (seeded from data/welcomeday/), read/
// written through /api/welcome-day. This module holds the pure shape helpers
// plus the load/save calls. Ported from UMapsTools (was a Vite dev-server +
// glob build); here it talks to the CRA/Express/Vercel API instead.

import { API_BASE, authHeaders } from './api.js';

const DEFAULT_DATA = { title: 'Welcome Day', subtitle: '', events: [], resources: [] };

let idSeq = 0;
const uid = (p) => `${p}_${Date.now().toString(36)}_${(idSeq++).toString(36)}`;

export function makeEvent(at = null) {
  return { id: uid('evt'), time: '09:00', title: '', location: '', description: '', icon: '📍',
    lat: at?.lat ?? null, lng: at?.lng ?? null, image: '',
    // Wayfinding: the destination room's nav node + ordered checkpoints (nav
    // nodes the route must pass through, e.g. a specific entrance door).
    destNodeId: null, checkpoints: [] };
}
export function makeResource() {
  // `place` (optional) positions the item inside the backpack pocket:
  // { x, y } are percentages of the pocket, `scale` a multiplier, `rot` degrees.
  // null ⇒ fall back to the preset scatter spots.
  // `destNodeId` + `location` (optional) let students wayfind to where the
  // resource is picked up — same nav-node model as an event's destination.
  return { id: uid('res'), name: '', icon: '🎒', synopsis: '', details: '', image: '', place: null,
    destNodeId: null, location: '' };
}

/** Normalize any loaded blob into the full shape. */
export function normalizeWelcomeDay(d) {
  const o = d && typeof d === 'object' ? d : {};
  return {
    title: o.title || 'Welcome Day',
    subtitle: o.subtitle || '',
    events: Array.isArray(o.events) ? o.events : [],
    resources: Array.isArray(o.resources) ? o.resources : [],
  };
}

/** "08:30" → minutes since midnight (for sorting); non-times sort last. */
export function timeToMinutes(t) {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(t || '').trim());
  return m ? Number(m[1]) * 60 + Number(m[2]) : 24 * 60 + 1;
}
export function sortedEvents(events) {
  return [...(events || [])].sort((a, b) => timeToMinutes(a.time) - timeToMinutes(b.time));
}
/** "14:05" → "2:05 PM" for display. */
export function prettyTime(t) {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(t || '').trim());
  if (!m) return t || '';
  let h = Number(m[1]); const min = m[2]; const ap = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${h}:${min} ${ap}`;
}

/** Load the Welcome Day itinerary (KV → bundled seed, both via the API). */
export async function loadWelcomeDay() {
  try {
    const res = await fetch(`${API_BASE}/api/welcome-day?part=welcome`);
    if (res.ok) return normalizeWelcomeDay(await res.json());
  } catch { /* API unreachable — show the empty default */ }
  return normalizeWelcomeDay(DEFAULT_DATA);
}

/** Persist the itinerary (requires the editor token). @returns {Promise<{ok,message?}>} */
export async function saveWelcomeDay(data) {
  const payload = normalizeWelcomeDay(data);
  try {
    const res = await fetch(`${API_BASE}/api/welcome-day`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ op: 'save-welcome', data: payload }),
    });
    if (res.ok) { const d = await res.json().catch(() => ({})); return { ok: true, store: d.store }; }
    if (res.status === 401) return { ok: false, message: 'Not signed in — enter the editor password again.' };
    return { ok: false, message: (await res.json().catch(() => ({}))).error || `Save failed (${res.status}).` };
  } catch (e) {
    return { ok: false, message: 'Save failed: ' + (e?.message || e) };
  }
}
